"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { GameResult } from "@/lib/chess/game";
import type { PlayableVariantId } from "@/lib/variants/config";
import type { VariantMove } from "@/lib/variants/rules";

export type VariantRecord = { w: number; d: number; l: number };

/**
 * How many moves remain after a takeback: back to the player's turn, one ply if the bot
 * has not answered yet, else two. Null when there is nothing to take back.
 */
export function variantTakebackLength(moves: readonly unknown[], startTurn: "w" | "b", playerColor: "w" | "b"): number | null {
  const sideAfter = (len: number) => ((len % 2 === 0) === (startTurn === "w") ? "w" : "b");
  for (let len = moves.length - 1; len >= 0; len--) if (sideAfter(len) === playerColor) return len;
  return null;
}

export type VariantGameState = {
  gameId: string;
  variantId: PlayableVariantId;
  startFen: string;
  /** Scharnagl number of a Chess960 start, for the record and the PGN. */
  chess960Index: number | null;
  moves: VariantMove[];
  playerColor: "w" | "b";
  levelId: string;
  status: "setup" | "playing" | "over";
  result: GameResult | null;
  /** Ply being viewed: null follows the live position. */
  cursor: number | null;
  takebacks: number;
  /** Your results per variant against the bot. There is no rating: variant bots have no Elo. */
  records: Partial<Record<PlayableVariantId, VariantRecord>>;

  chooseVariant: (id: PlayableVariantId) => void;
  newGame: (game: { variantId: PlayableVariantId; startFen: string; chess960Index: number | null; color: "w" | "b"; levelId: string }) => void;
  addMove: (move: VariantMove, result: GameResult | null) => void;
  takeback: () => void;
  resign: () => void;
  setCursor: (c: number | null) => void;
  backToSetup: () => void;
};

function withResult(s: VariantGameState, result: GameResult): Partial<VariantGameState> {
  const prior = s.records[s.variantId] ?? { w: 0, d: 0, l: 0 };
  const key = result.winner === null ? "d" : result.winner === s.playerColor ? "w" : "l";
  return { status: "over", result, cursor: null, records: { ...s.records, [s.variantId]: { ...prior, [key]: prior[key] + 1 } } };
}

export const useVariantStore = create<VariantGameState>()(
  persist(
    (set, get) => ({
      gameId: "none",
      variantId: "chess960",
      startFen: "",
      chess960Index: null,
      moves: [],
      playerColor: "w",
      levelId: "casual",
      status: "setup",
      result: null,
      cursor: null,
      takebacks: 0,
      records: {},

      chooseVariant: (variantId) => set({ variantId }),

      newGame: ({ variantId, startFen, chess960Index, color, levelId }) =>
        set({
          gameId: crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
          variantId, startFen, chess960Index, levelId,
          playerColor: color,
          moves: [], status: "playing", result: null, cursor: null, takebacks: 0,
        }),

      addMove: (move, result) => {
        const s = get();
        if (s.status !== "playing") return;
        const next = { ...s, moves: [...s.moves, move] };
        set({ moves: next.moves, cursor: null, ...(result ? withResult(next, result) : {}) });
      },

      takeback: () => {
        const s = get();
        if (s.status !== "playing") return;
        const startTurn = s.startFen.split(" ")[1] === "b" ? "b" : "w";
        const len = variantTakebackLength(s.moves, startTurn, s.playerColor);
        if (len === null) return;
        set({ moves: s.moves.slice(0, len), cursor: null, takebacks: s.takebacks + 1 });
      },

      resign: () => {
        const s = get();
        if (s.status !== "playing") return;
        set(withResult(s, { winner: s.playerColor === "w" ? "b" : "w", reason: "resignation" }));
      },

      setCursor: (cursor) => set({ cursor: cursor === null ? null : Math.max(0, Math.min(get().moves.length, Math.trunc(cursor))) }),
      backToSetup: () => set({ status: "setup", cursor: null }),
    }),
    { name: "atlas:variants", version: 1, skipHydration: true },
  ),
);
